import type { HeadlinedItem, ReflectionV2 } from '@/store/api/mcpMemberApi';
import type { MomentsReflectionV2SourceIdentity } from '@/store/api/momentsApi';

export type ReflectionV2SectionKey =
  | 'key_insight'
  | 'realized'
  | 'strengths'
  | 'opportunities'
  | 'guidance'
  | 'actions'
  | 'questions';

export interface ReflectionV2Item {
  headline?: string;
  key: string;
  text: string;
}

export interface ReflectionV2Section {
  bullet: boolean;
  items: ReflectionV2Item[];
  key: ReflectionV2SectionKey;
  title: string;
}

// Section headings from the prototype viewReflection().
const SECTION_TITLES: Record<ReflectionV2SectionKey, string> = {
  actions: 'Suggested Action Items',
  guidance: 'Guidance to Carry Forward',
  key_insight: 'Key Insight',
  opportunities: 'Leadership Opportunity',
  questions: 'Reflection',
  realized: 'What You Realized',
  strengths: 'Strengths Already Visible',
};

// Spec §4.1: an item's stable highlight key is `<section>-<index>`.
export const highlightKey = (
  section: ReflectionV2SectionKey,
  index: number,
): string => `${section}-${index}`;

const headlined = (
  key: ReflectionV2SectionKey,
  items: HeadlinedItem[],
): ReflectionV2Section => ({
  bullet: false,
  items: items.map((item, index) => ({
    headline: item.headline,
    key: highlightKey(key, index),
    text: item.text,
  })),
  key,
  title: SECTION_TITLES[key],
});

const plain = (
  key: ReflectionV2SectionKey,
  items: string[],
  bullet = false,
): ReflectionV2Section => ({
  bullet,
  items: items.map((text, index) => ({ key: highlightKey(key, index), text })),
  key,
  title: SECTION_TITLES[key],
});

export const reflectionV2Sections = (r: ReflectionV2): ReflectionV2Section[] =>
  [
    plain('key_insight', r.key_insight ? [r.key_insight] : []),
    headlined('realized', r.realized),
    headlined('strengths', r.strengths),
    headlined('opportunities', r.opportunities),
    plain('guidance', r.guidance),
    plain('actions', r.actions, true),
    plain('questions', r.questions),
  ].filter((section) => section.items.length > 0);

// Contracts §8.11 selected_text: headline + "\n" + text for realized/strengths/
// opportunities; the plain string for other sections; the key insight for key_insight-0.
export const highlightText = (item: ReflectionV2Item): string =>
  item.headline ? `${item.headline}\n${item.text}` : item.text;

// Contracts §8.11: Plan A's dedicated v2 note identity.
export const v2SourceIdentity = (
  revision: number,
): MomentsReflectionV2SourceIdentity => ({
  anchorContract: 'reflection-v2-key-v1',
  readerContract: 'reflection-v2-item-v1',
  revision,
  scope: 'whole-block',
});

// Prototype copyRefl: Key Insight, What You Realized, Guidance to Carry Forward.
export const reflectionCopyText = (r: ReflectionV2): string =>
  [
    'Key Insight',
    r.key_insight,
    '',
    'What You Realized',
    ...r.realized.map((item) => `${item.headline}\n${item.text}`),
    '',
    'Guidance to Carry Forward',
    ...r.guidance,
  ].join('\n');
